import React, { useMemo, useState, useEffect } from 'react';
import Alert from 'src/components/alert';
import Divider from 'src/components/divider';
import { FormCombobox } from 'src/components/form-combobox';
import FormInput from 'src/components/form-input';
import FormSwitch from 'src/components/form-switch';
import IPInput from 'src/components/ip-input';
import LoadingSpinner from 'src/components/loading/spinner';
import { useGlobalContext } from 'src/context/global/use-global-context';
import {
  NodeCategoryModel,
  IDhcpServerSettings,
  IDhcpServerValidationErrors,
} from 'src/modules/network-configuration/models/node-data';
import { convertSubnetMaskToLength } from 'src/utils/ip';
import { validateDhcpServer } from 'src/utils/validate';

import { INodeEditData } from './type';

const defaultDhcpServerSettings: IDhcpServerSettings = {
  leaseTime: 86400,
  poolEnabled: false,
  poolStart: '',
  poolEnd: '',
  routerIp1: '',
  routerIp2: '',
  domainServerIp1: '',
  domainServerIp2: '',
  domainName: '',
  ntpServerIp1: '',
  ntpServerIp2: '',
};

interface IProps {
  id: string;
  data: NodeCategoryModel;
  values: INodeEditData;
  setValues: React.Dispatch<React.SetStateAction<INodeEditData>>;
  /** From node.data.category.validationErrors.dhcpServer (validateNode) */
  errors?: IDhcpServerValidationErrors;
  forceRefresh?: number;
}

export default function EditDhcpServer({
  id,
  data,
  values,
  setValues,
  errors = {},
  forceRefresh = 0,
}: IProps) {
  const hasVlan = useMemo(
    () => (values.ipInterfaces?.length ?? 0) > 0,
    [values.ipInterfaces],
  );

  return (
    <div
      data-testid={`edit-dhcp-server-${id}`}
      className="flex flex-col items-center justify-start gap-0"
    >
      {hasVlan ? (
        <EditDhcpServerVlan
          id={id}
          data={data}
          values={values}
          setValues={setValues}
          errors={errors}
          forceRefresh={forceRefresh || 0}
        />
      ) : (
        <div className="mt-2">
          <EditDhcpServerWithoutVlan
            id={id}
            data={data}
            values={values}
            setValues={setValues}
            errors={errors}
            forceRefresh={forceRefresh || 0}
          />
        </div>
      )}
    </div>
  );
}

function EditDhcpServerVlan({
  id,
  data,
  values,
  setValues,
  errors = {},
  forceRefresh = 0,
}: IProps) {
  const ipInterfaces = useMemo(
    () => values.ipInterfaces || [],
    [values.ipInterfaces],
  );

  const [selectedVlanId, setSelectedVlanId] = useState<number>(
    () => ipInterfaces[0]?.vlanId ?? 0,
  );
  const { vlans } = useGlobalContext();
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // VLAN options for dropdown
  const vlanOptions = useMemo(
    () =>
      ipInterfaces.map((vlan) => {
        const vlanItem = vlans?.find(
          (v) => v.vlanId.toString() === vlan.vlanId.toString(),
        );
        return {
          label: vlan.vlanId.toString(),
          value: vlan.vlanId.toString(),
          color: vlanItem?.color,
          description: vlanItem?.description,
          caption: `${vlan.subnet}/${convertSubnetMaskToLength(vlan.subnetMask || '')}`,
          displayText: `${vlan.vlanId} - ${vlanItem?.description} (${vlan.subnet}/${convertSubnetMaskToLength(vlan.subnetMask || '')})`,
        };
      }),
    [ipInterfaces, vlans],
  );

  // Keep selection in sync when options change (e.g. selected id no longer in list)
  const valueInOptions = vlanOptions.some(
    (o) => o.value === String(selectedVlanId),
  )
    ? String(selectedVlanId)
    : (vlanOptions[0]?.value ?? '');
  useEffect(() => {
    if (valueInOptions && parseInt(valueInOptions, 10) !== selectedVlanId) {
      setSelectedVlanId(parseInt(valueInOptions, 10));
    }
  }, [valueInOptions, selectedVlanId]);

  if (ipInterfaces.length === 0) {
    return (
      <div className="mt-2">
        <p className="text-sm text-gray-600">
          No VLANs with IP subnets are configured
        </p>
      </div>
    );
  }

  return (
    <div data-testid={`edit-dhcp-server-vlan-${id}`}>
      <div className="mt-4 space-y-4">
        <FormCombobox
          label="DHCP Server Settings for IP Subnet"
          anchor="bottom start"
          className="w-full flex-row items-center gap-2"
          optionWrapperClassName="w-84"
          inputWrapperClassName="w-auto flex-1"
          options={vlanOptions}
          value={valueInOptions}
          onChange={(value) => {
            setSelectedVlanId(parseInt(value, 10));
            // trigger loading for refreshing the data
            setIsLoading(true);
            setTimeout(() => {
              setIsLoading(false);
            }, 100);
          }}
          dataTestId="dialog-dhcp-server-vlan-select"
        />
        <Divider />
        {isLoading ? (
          <div className="flex flex-col items-center justify-center">
            <LoadingSpinner />
          </div>
        ) : (
          <EditDhcpServerDetails
            settings={{
              ...defaultDhcpServerSettings,
              ...(ipInterfaces.find((vlan) => vlan.vlanId === selectedVlanId)
                ?.dhcpServer || {}),
            }}
            errors={errors}
            onChange={(newData) => {
              setValues((prev) => ({
                ...prev,
                ipInterfaces: (prev.ipInterfaces || []).map((vlan) =>
                  vlan.vlanId === selectedVlanId
                    ? { ...vlan, dhcpServer: newData }
                    : vlan,
                ),
              }));
            }}
            forceRefresh={forceRefresh}
          />
        )}
      </div>
    </div>
  );
}

function EditDhcpServerWithoutVlan({
  id,
  data,
  values,
  setValues,
  errors = {},
  forceRefresh = 0,
}: IProps) {
  return (
    <div data-testid={`edit-dhcp-server-without-vlan-${id}`}>
      <EditDhcpServerDetails
        settings={{
          ...defaultDhcpServerSettings,
          ...(values.dhcpServer || data.dhcpServer || {}),
        }}
        errors={errors}
        onChange={(newData) => {
          setValues((prev) => ({ ...prev, dhcpServer: newData }));
        }}
        forceRefresh={forceRefresh || 0}
      />
    </div>
  );
}

interface IEditDhcpServerDetailsProps {
  settings: IDhcpServerSettings;
  onChange: (newData: IDhcpServerSettings) => void;
  errors?: IDhcpServerValidationErrors;
  forceRefresh?: number;
}

function EditDhcpServerDetails({
  settings,
  onChange,
  errors = {},
  forceRefresh = 0,
}: IEditDhcpServerDetailsProps) {
  const internalErrors = useMemo(
    () => validateDhcpServer(settings),
    [settings],
  );

  const finalErrors = useMemo(
    () => ({
      leaseTime: errors.leaseTime || internalErrors.leaseTime,
      poolStart: errors.poolStart || internalErrors.poolStart,
      poolEnd: errors.poolEnd || internalErrors.poolEnd,
      routerIp1: errors.routerIp1 || internalErrors.routerIp1,
      routerIp2: errors.routerIp2 || internalErrors.routerIp2,
      domainServerIp1: errors.domainServerIp1 || internalErrors.domainServerIp1,
      domainServerIp2: errors.domainServerIp2 || internalErrors.domainServerIp2,
      domainName: errors.domainName || internalErrors.domainName,
      ntpServerIp1: errors.ntpServerIp1 || internalErrors.ntpServerIp1,
      ntpServerIp2: errors.ntpServerIp2 || internalErrors.ntpServerIp2,
    }),
    [errors, internalErrors],
  );

  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    if (forceRefresh) {
      setIsLoading(true);
      setTimeout(() => {
        setIsLoading(false);
      }, 100);
    }
  }, [forceRefresh]);
  const handleLeaseTimeChange = (val: string) => {
    const numValue = parseInt(val, 10) || 0;
    const next = { ...settings, leaseTime: numValue };
    onChange(next);
  };

  const handleIpChange = (
    val: string,
    fieldName:
      | 'poolStart'
      | 'poolEnd'
      | 'routerIp1'
      | 'routerIp2'
      | 'domainServerIp1'
      | 'domainServerIp2'
      | 'ntpServerIp1'
      | 'ntpServerIp2',
  ) => {
    const next = { ...settings, [fieldName]: val };
    onChange(next);
  };

  const handleDomainNameChange = (val: string) => {
    const next = { ...settings, domainName: val };
    onChange(next);
  };

  const handlePoolEnabledChange = (enabled: boolean) => {
    const next = { ...settings, poolEnabled: enabled };
    onChange(next);
  };

  useEffect(() => {
    if (forceRefresh) {
      setIsLoading(true);
      setTimeout(() => {
        setIsLoading(false);
      }, 100);
    }
  }, [forceRefresh]);

  const hasChanged = useMemo(
    () =>
      JSON.stringify(settings) !== JSON.stringify(defaultDhcpServerSettings),
    [settings],
  );

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col items-start justify-start">
        <h3 className="text-sm font-semibold text-gray-900">Lease Time</h3>
        <div className="flex flex-row items-start justify-start gap-0">
          <span className="w-32 text-sm text-gray-900">in seconds</span>
          <div className="flex flex-1">
            <FormInput
              id="lease-time"
              name="leaseTime"
              wrapperClassName="flex-row"
              type="number"
              value={settings.leaseTime.toString()}
              onChange={(e) => handleLeaseTimeChange(e.target.value)}
              error={finalErrors.leaseTime}
              min="1"
              data-testid="dialog-dhcp-server-lease-time"
            />
          </div>
        </div>
      </div>
      <div className="space-y-2">
        <h3 className="text-sm font-semibold text-gray-900">IP Address Pool</h3>
        <div className="flex flex-row items-center justify-start gap-0">
          <span className="w-32 text-sm text-gray-900">enabled</span>
          <FormSwitch
            id="pool-enabled"
            wrapperClassName="w-auto"
            checked={settings.poolEnabled}
            onChange={handlePoolEnabledChange}
            data-testid="dialog-dhcp-server-pool-enabled"
          />
        </div>
        <div className="flex flex-row items-center justify-start gap-0">
          <span className="w-32 text-sm text-gray-900">range</span>
          <span className="w-9 text-sm text-gray-900">from</span>
          <IPInput
            id="pool-start"
            wrapperClassName="w-auto"
            value={settings.poolStart || ''}
            onChange={(value) => handleIpChange(value, 'poolStart')}
            error={finalErrors.poolStart}
            disabled={!settings.poolEnabled}
            placeholder="192.168.1.100"
            dataTestId="dialog-dhcp-server-pool-start"
          />
          <div className="w-8" />
          <span className="w-9 text-sm text-gray-900">to</span>
          <IPInput
            id="pool-end"
            wrapperClassName="w-auto"
            value={settings.poolEnd || ''}
            onChange={(value) => handleIpChange(value, 'poolEnd')}
            error={finalErrors.poolEnd}
            disabled={!settings.poolEnabled}
            placeholder="192.168.1.200"
            dataTestId="dialog-dhcp-server-pool-end"
          />
        </div>
      </div>
      <div className="space-y-2">
        <h3 className="text-sm font-semibold text-gray-900">DHCP Options</h3>
        <div className="flex flex-row items-center justify-start gap-0">
          <span className="w-32 text-sm text-gray-900">Router IP</span>
          <span className="w-9 text-sm text-gray-900">1.</span>
          <IPInput
            id="router-ip-1"
            wrapperClassName="w-auto"
            value={settings.routerIp1 || ''}
            onChange={(value) => handleIpChange(value, 'routerIp1')}
            error={finalErrors.routerIp1}
            placeholder="192.168.1.254"
            dataTestId="dialog-dhcp-server-router-ip-1"
          />
          <div className="w-8" />
          <span className="w-9 text-sm text-gray-900">2.</span>
          <IPInput
            id="router-ip-2"
            wrapperClassName="w-auto"
            value={settings.routerIp2 || ''}
            onChange={(value) => handleIpChange(value, 'routerIp2')}
            error={finalErrors.routerIp2}
            placeholder="192.168.1.253"
            dataTestId="dialog-dhcp-server-router-ip-2"
          />
        </div>

        <div className="flex flex-row items-center justify-start gap-0">
          <span className="w-32 text-sm text-gray-900">Domain Server IP</span>
          <span className="w-9 text-sm text-gray-900">1.</span>
          <IPInput
            id="domain-server-ip-1"
            wrapperClassName="w-auto"
            value={settings.domainServerIp1 || ''}
            onChange={(value) => handleIpChange(value, 'domainServerIp1')}
            error={finalErrors.domainServerIp1}
            placeholder="192.168.1.254"
            dataTestId="dialog-dhcp-server-domain-server-ip-1"
          />
          <div className="w-8" />
          <span className="w-9 text-sm text-gray-900">2.</span>
          <IPInput
            id="domain-server-ip-2"
            wrapperClassName="w-auto"
            value={settings.domainServerIp2 || ''}
            onChange={(value) => handleIpChange(value, 'domainServerIp2')}
            error={finalErrors.domainServerIp2}
            placeholder="192.168.1.253"
            dataTestId="dialog-dhcp-server-domain-server-ip-2"
          />
        </div>

        <div className="flex flex-row items-center justify-start gap-0">
          <span className="w-32 text-sm text-gray-900">Domain Name</span>
          <FormInput
            id="domain-name"
            name="domainName"
            wrapperClassName="w-auto"
            value={settings.domainName || ''}
            onChange={(e) => handleDomainNameChange(e.target.value)}
            error={finalErrors.domainName}
            data-testid="dialog-dhcp-server-domain-name"
          />
        </div>

        <div className="flex flex-row items-center justify-start gap-0">
          <span className="w-32 text-sm text-gray-900">NTP Server IP</span>
          <span className="w-9 text-sm text-gray-900">1.</span>
          <IPInput
            id="ntp-server-ip-1"
            wrapperClassName="w-auto"
            value={settings.ntpServerIp1 || ''}
            onChange={(value) => handleIpChange(value, 'ntpServerIp1')}
            error={finalErrors.ntpServerIp1}
            placeholder="192.168.1.254"
            dataTestId="dialog-dhcp-server-ntp-server-ip-1"
          />
          <div className="w-8" />
          <span className="w-9 text-sm text-gray-900">2.</span>
          <IPInput
            id="ntp-server-ip-2"
            wrapperClassName="w-auto"
            value={settings.ntpServerIp2 || ''}
            onChange={(value) => handleIpChange(value, 'ntpServerIp2')}
            error={finalErrors.ntpServerIp2}
            placeholder="192.168.1.253"
            dataTestId="dialog-dhcp-server-ntp-server-ip-2"
          />
        </div>
      </div>
      {hasChanged && (
        <Alert
          type="warning"
          title="Warning"
          description="Invalid configurations may be discarded, please check after saving."
        />
      )}
    </div>
  );
}
